import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, CalendarDays, Plus, Trash2, Copy, Check, 
  Loader2, AlertCircle, CalendarIcon, Phone, User, Shield,
  Link2, Clock, UserPlus, ExternalLink
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');

  return (
    <div className="pb-24" data-testid="admin-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-4 py-4 sticky top-0 z-10">
        <h1 className="font-serif text-2xl text-slate-900">Admin Panel</h1>
        <p className="text-slate-500 text-sm">Manage users and events</p>
      </div>

      <div className="p-4">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2 w-full h-12 rounded-2xl bg-slate-100 p-1">
            <TabsTrigger 
              value="users" 
              className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger 
              value="events"
              className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <CalendarDays className="w-4 h-4 mr-2" />
              Events
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-4">
            <UsersTab currentUserId={user?.id} />
          </TabsContent>

          <TabsContent value="events" className="mt-4">
            <EventsTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const UsersTab = ({ currentUserId }) => {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [newInviteLink, setNewInviteLink] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, invitesRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/invites`)
      ]);
      setUsers(usersRes.data);
      setInvites(invitesRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      toast.success('User deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const deleteInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/invites/${inviteId}`);
      setInvites(invites.filter(i => i.id !== inviteId));
      toast.success('Invite deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invite');
    }
  };

  const createInvite = async (expiresInHours = 48) => {
    try {
      const response = await axios.post(`${API}/invites`, { expires_in_hours: expiresInHours });
      const invite = response.data;
      setInvites([invite, ...invites]);
      
      // Generate full URL
      const inviteUrl = `${window.location.origin}/register/${invite.token}`;
      setNewInviteLink({ ...invite, url: inviteUrl });
      
      toast.success('Invite link created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Generate Invite Link Section */}
      <div className="bg-violet-50 rounded-3xl p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
            <UserPlus className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h3 className="font-medium text-slate-900">Invite New Members</h3>
            <p className="text-sm text-slate-500">Generate a link for users to register</p>
          </div>
        </div>
        
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="generate-invite-btn"
              className="w-full h-12 rounded-2xl bg-violet-600 hover:bg-violet-700 text-white"
            >
              <Link2 className="w-5 h-5 mr-2" />
              Generate Invite Link
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Generate Invite Link</DialogTitle>
            </DialogHeader>
            <GenerateInviteForm 
              onSuccess={(invite) => {
                const inviteUrl = `${window.location.origin}/register/${invite.token}`;
                setNewInviteLink({ ...invite, url: inviteUrl });
                setInvites([invite, ...invites]);
                setInviteDialogOpen(false);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* New Invite Link Card */}
      {newInviteLink && (
        <InviteLinkCard 
          invite={newInviteLink}
          onDismiss={() => setNewInviteLink(null)}
        />
      )}

      {/* Pending Invites */}
      {invites.filter(i => !i.used).length > 0 && (
        <div>
          <h3 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Pending Invites
          </h3>
          <div className="space-y-2">
            {invites.filter(i => !i.used).map(invite => (
              <InviteCard 
                key={invite.id} 
                invite={invite}
                onDelete={() => deleteInvite(invite.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Users List */}
      <div>
        <h3 className="font-medium text-slate-700 mb-3 flex items-center gap-2">
          <Users className="w-4 h-4" />
          Registered Users ({users.length})
        </h3>
        <div className="space-y-2">
          {users.map(user => (
            <UserCard 
              key={user.id} 
              user={user} 
              isCurrent={user.id === currentUserId}
              onDelete={() => deleteUser(user.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const GenerateInviteForm = ({ onSuccess }) => {
  const [expiresIn, setExpiresIn] = useState('48');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/invites`, { 
        expires_in_hours: parseInt(expiresIn) 
      });
      onSuccess(response.data);
      toast.success('Invite link created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-600 mb-2">
          Link expires in
        </label>
        <Select value={expiresIn} onValueChange={setExpiresIn}>
          <SelectTrigger className="h-12 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24">24 hours</SelectItem>
            <SelectItem value="48">48 hours</SelectItem>
            <SelectItem value="72">72 hours (3 days)</SelectItem>
            <SelectItem value="168">1 week</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Button
        data-testid="create-invite-btn"
        type="submit"
        disabled={loading}
        className="w-full h-12 rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Invite Link'}
      </Button>
    </form>
  );
};

const InviteLinkCard = ({ invite, onDismiss }) => {
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(invite.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copied!');
  };

  return (
    <div className="bg-green-50 border border-green-200 rounded-2xl p-4 animate-slide-up">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Check className="w-5 h-5 text-green-600" />
          <span className="font-medium text-green-800">Invite Link Ready</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDismiss}
          className="text-slate-400 hover:text-slate-600"
        >
          Dismiss
        </Button>
      </div>
      
      <div className="bg-white rounded-xl p-3 mb-3">
        <p className="text-sm text-slate-500 mb-1">Share this link:</p>
        <p className="text-sm font-mono text-slate-900 break-all">{invite.url}</p>
      </div>
      
      <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
        <Clock className="w-4 h-4" />
        Expires {formatDistanceToNow(new Date(invite.expires_at), { addSuffix: true })}
      </div>

      <Button
        onClick={copyLink}
        className="w-full h-10 rounded-xl bg-green-600 hover:bg-green-700 text-white"
      >
        {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
        {copied ? 'Copied!' : 'Copy Link'}
      </Button>
    </div>
  );
};

const InviteCard = ({ invite, onDelete }) => {
  const [copied, setCopied] = useState(false);
  const inviteUrl = `${window.location.origin}/register/${invite.token}`;
  const isExpired = new Date(invite.expires_at) < new Date();

  const copyLink = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Link copied!');
  };

  return (
    <div className={`bg-white rounded-2xl p-4 shadow-sm ${isExpired ? 'opacity-60' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-violet-500" />
          <span className="text-sm font-medium text-slate-700">
            {isExpired ? 'Expired' : 'Active'}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {!isExpired && (
            <Button
              variant="ghost"
              size="icon"
              onClick={copyLink}
              className="h-8 w-8 text-slate-400 hover:text-violet-600"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onDelete}
            className="h-8 w-8 text-slate-400 hover:text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      <div className="text-xs text-slate-500">
        <p>Created {formatDistanceToNow(new Date(invite.created_at), { addSuffix: true })}</p>
        <p>Expires {formatDistanceToNow(new Date(invite.expires_at), { addSuffix: true })}</p>
      </div>
    </div>
  );
};

const UserCard = ({ user, isCurrent, onDelete }) => {
  return (
    <div 
      data-testid={`user-card-${user.id}`}
      className="bg-white rounded-2xl p-4 shadow-sm flex items-center gap-4"
    >
      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
        user.role === 'admin' ? 'bg-violet-100' : 'bg-slate-100'
      }`}>
        {user.role === 'admin' ? (
          <Shield className="w-5 h-5 text-violet-600" />
        ) : (
          <User className="w-5 h-5 text-slate-500" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-slate-900 truncate">{user.name}</p>
          {user.role === 'admin' && (
            <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">
              Admin
            </span>
          )}
        </div>
        <p className="text-sm text-slate-500 flex items-center gap-1">
          <Phone className="w-3 h-3" />
          {user.phone}
        </p>
      </div>
      {!isCurrent && (
        <Button
          data-testid={`delete-user-${user.id}`}
          variant="ghost"
          size="icon"
          onClick={onDelete}
          className="text-slate-400 hover:text-red-500 hover:bg-red-50"
        >
          <Trash2 className="w-5 h-5" />
        </Button>
      )}
    </div>
  );
};

const EventsTab = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axios.delete(`${API}/events/${eventId}`);
      setEvents(events.filter(e => e.id !== eventId));
      toast.success('Event deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add Event Button */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogTrigger asChild>
          <Button
            data-testid="add-event-btn"
            className="w-full h-12 rounded-2xl bg-violet-600 hover:bg-violet-700 text-white"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add New Event
          </Button>
        </DialogTrigger>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Add New Event</DialogTitle>
          </DialogHeader>
          <AddEventForm 
            onSuccess={(event) => {
              setEvents([event, ...events]);
              setAddDialogOpen(false);
              toast.success('Event created');
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Events List */}
      {events.length === 0 ? (
        <div className="text-center py-12">
          <CalendarDays className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No events yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(event => (
            <EventCard 
              key={event.id} 
              event={event}
              onDelete={() => deleteEvent(event.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const EventCard = ({ event, onDelete }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short',
      year: 'numeric'
    });
  };

  const isPast = new Date(event.date) < new Date();

  return (
    <div 
      data-testid={`event-card-${event.id}`}
      className={`bg-white rounded-2xl p-4 shadow-sm flex items-center gap-4 ${isPast ? 'opacity-70' : ''}`}
    >
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
        isPast ? 'bg-slate-100' : 'bg-violet-100'
      }`}>
        <CalendarDays className={`w-5 h-5 ${isPast ? 'text-slate-400' : 'text-violet-600'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 truncate">{event.title}</p>
        <p className="text-sm text-slate-500">{formatDate(event.date)}</p>
        {event.photo_count > 0 && (
          <p className="text-xs text-violet-600 mt-1">{event.photo_count} photos</p>
        )}
      </div>
      {event.photo_count === 0 && (
        <Button
          data-testid={`delete-event-${event.id}`}
          variant="ghost"
          size="icon"
          onClick={onDelete}
          className="text-slate-400 hover:text-red-500 hover:bg-red-50"
        >
          <Trash2 className="w-5 h-5" />
        </Button>
      )}
    </div>
  );
};

const AddEventForm = ({ onSuccess }) => {
  const [date, setDate] = useState(new Date());
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!date || !title.trim()) {
      setError('Please fill all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/events`, {
        date: format(date, 'yyyy-MM-dd'),
        title: title.trim()
      });
      onSuccess(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-slate-600 mb-1">Event Date</label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full h-12 justify-start text-left font-normal rounded-xl"
            >
              <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
              {date ? format(date, 'PPP') : 'Select date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-600 mb-1">Event Title</label>
        <Input
          data-testid="new-event-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Sunday Service, Youth Night"
          className="h-12 rounded-xl"
        />
      </div>

      {error && (
        <div className="text-red-500 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      <Button
        data-testid="create-event-btn"
        type="submit"
        disabled={loading}
        className="w-full h-12 rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Event'}
      </Button>
    </form>
  );
};

export default AdminPage;
